import Api from '../components/api/api';

async function getKeyMaterial(passphrase: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  return await crypto.subtle.importKey('raw', encoder.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
}

async function deriveKey(keyMaterial: CryptoKey, salt: Uint8Array): Promise<CryptoKey> {
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

async function encryptData(data: ArrayBuffer, key: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
  return await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
}

export async function bufferToFile(data: ArrayBuffer | Uint8Array) {
  return new Blob([data], { type: 'application/octet-stream' });
}

export async function decryptFile(encryptedData: ArrayBuffer, secretPhrase: string): Promise<ArrayBuffer> {
  const salt = new Uint8Array(encryptedData.slice(0, 16));
  const iv = new Uint8Array(encryptedData.slice(16, 28));
  const encryptedContent = encryptedData.slice(28);

  const keyMaterial = await getKeyMaterial(secretPhrase);
  const decryptionKey = await deriveKey(keyMaterial, salt);

  return await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, decryptionKey, encryptedContent);
}

export async function encryptFile(file: File, secretPhrase: string) {
  try {
    const fileData = await file.arrayBuffer();
    const keyMaterial = await getKeyMaterial(secretPhrase);
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const encryptionKey = await deriveKey(keyMaterial, salt);

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await encryptData(fileData, encryptionKey, iv);

    const combinedData = new Uint8Array(salt.byteLength + iv.byteLength + encryptedData.byteLength);
    combinedData.set(salt, 0);
    combinedData.set(iv, salt.byteLength);
    combinedData.set(new Uint8Array(encryptedData), salt.byteLength + iv.byteLength);

    const encryptedFile = new File([combinedData], `${file.name}.enc`, {
      type: file.type,
    });

    await Api().updloadFile(encryptedFile as File);
  } catch (error) {
    console.error('Encryption failed:', error);
  }
}
